import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, DownloadIcon, LayoutGridIcon } from 'lucide-react';
import { storyboardFrames, StoryboardFrame, StoryboardMotif } from '../data/storyboard';
interface StoryboardProps {
  open: boolean;
  onClose: () => void;
}
const PURPLE = '#6C2BD9';
function Motif({
  variant
}: {
  variant: StoryboardMotif;
}) {
  const common = 'w-full h-full';
  switch (variant) {
    case 'title':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-922" data-mp="3n0nydpz2xw">
          <circle cx="16" cy="24" r="9" fill={PURPLE} data-id="element-923" data-mp="53svtoj777kq" />
          <rect x="32" y="16" width="60" height="5" rx="2.5" fill={PURPLE} opacity="0.5" data-id="element-924" data-mp="3md7g6ro5ofwf" />
          <rect x="32" y="26" width="40" height="5" rx="2.5" fill={PURPLE} opacity="0.2" data-id="element-925" data-mp="3rbisd91xb2ht" />
        </svg>;
    case 'curve':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-926" data-mp="2avzck8lc33tb">
          <path d="M4 44 C 20 44, 24 8, 44 8 C 62 8, 62 34, 80 38 C 96 41, 104 40, 116 40" fill="none" stroke={PURPLE} strokeWidth="2.5" strokeLinecap="round" data-id="element-927" data-mp="2fxonzpcgxjhx" />
          <rect x="80" y="6" width="36" height="36" fill={PURPLE} opacity="0.08" data-id="element-928" data-mp="3ooeffgvfxw6t" />
        </svg>;
    case 'donut':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-929" data-mp="1i3xhioh5usv">
          <circle cx="24" cy="24" r="16" fill="none" stroke={PURPLE} strokeOpacity="0.18" strokeWidth="8" data-id="element-930" data-mp="1jylx6yyg51p" />
          <circle cx="24" cy="24" r="16" fill="none" stroke={PURPLE} strokeWidth="8" strokeDasharray="35 100" strokeLinecap="round" transform="rotate(-90 24 24)" data-id="element-931" data-mp="gcs10rabej5u" />
          <rect x="52" y="14" width="60" height="5" rx="2.5" fill={PURPLE} opacity="0.45" data-id="element-932" data-mp="l6mps94s1obs" />
          <rect x="52" y="26" width="38" height="5" rx="2.5" fill={PURPLE} opacity="0.18" data-id="element-933" data-mp="z5vw8h36l42k" />
        </svg>;
    case 'cliff':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-934" data-mp="3nyrd09u37ouc">
          <path d="M4 10 H 46 V 38 H 116" fill="none" stroke={PURPLE} strokeWidth="2.5" strokeLinecap="round" data-id="element-935" data-mp="3nwobotnhi9ab" />
          <line x1="46" y1="10" x2="46" y2="38" stroke={PURPLE} strokeWidth="2.5" strokeDasharray="3 3" data-id="element-936" data-mp="ts0sh04n5a5" />
          <circle cx="46" cy="38" r="3.5" fill={PURPLE} data-id="element-937" data-mp="5k8cnwoauiue" />
        </svg>;
    case 'calculator':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-938" data-mp="ake87nxowphr">
          <rect x="6" y="6" width="42" height="36" rx="5" fill={PURPLE} opacity="0.1" data-id="element-939" data-mp="fio27v7ohk1s" />
          <rect x="12" y="12" width="30" height="8" rx="2" fill={PURPLE} opacity="0.55" data-id="element-940" data-mp="fkis7nxt42cq" />
          {[0, 1, 2].map(r => [0, 1, 2].map(c => <rect key={`${r}-${c}`} x={12 + c * 10} y={24 + r * 6} width="7" height="4" rx="1.5" fill={PURPLE} opacity="0.3" data-id="element-941" data-mp="oi2vt3j56aqi" />))}
          <rect x="58" y="14" width="54" height="6" rx="3" fill={PURPLE} opacity="0.45" data-id="element-942" data-mp="2irzwtbbhcy3x" />
          <rect x="58" y="26" width="34" height="6" rx="3" fill={PURPLE} opacity="0.18" data-id="element-943" data-mp="2dzggw7pblqvy" />
        </svg>;
    case 'levers':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-944" data-mp="28z28gojt4exe">
          {[18, 40, 62, 84].map((x, i) => <g key={x} data-id="element-945" data-mp="31pu15c1nylfz" data-mpi={i}>
              <line x1={x} y1="8" x2={x} y2="40" stroke={PURPLE} strokeOpacity="0.2" strokeWidth="3" strokeLinecap="round" data-id="element-946" data-mp="31qc750e4oe13" />
              <circle cx={x} cy={i % 2 === 0 ? 16 : 32} r="4.5" fill={PURPLE} data-id="element-947" data-mp="2wris33j70eyt" />
            </g>)}
        </svg>;
    case 'stack':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-948" data-mp="2nque28tsuelx">
          {[8, 20, 32].map((y, i) => <rect key={y} x="10" y={y} width="100" height="9" rx="3" fill={PURPLE} opacity={0.5 - i * 0.15} data-id="element-949" data-mp="1gdskvnws1aky" data-mpi={i} />)}
        </svg>;
    case 'category':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-950" data-mp="34zlsi1wk5l3y">
          <rect x="6" y="14" width="34" height="20" rx="4" fill="none" stroke={PURPLE} strokeOpacity="0.3" strokeWidth="2" strokeDasharray="4 3" data-id="element-951" data-mp="2qa660y4281u2" />
          <rect x="80" y="14" width="34" height="20" rx="4" fill="none" stroke={PURPLE} strokeOpacity="0.3" strokeWidth="2" strokeDasharray="4 3" data-id="element-952" data-mp="2v9ah5uajo9n1" />
          <rect x="44" y="9" width="32" height="30" rx="5" fill={PURPLE} data-id="element-953" data-mp="3nx67mh9it5bd" />
        </svg>;
    case 'threeUp':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-954" data-mp="39wlrxtywjpm3">
          {[10, 46, 82].map((x, i) => <g key={x} data-id="element-955" data-mp="3dxnqs9owpznj" data-mpi={i}>
              <rect x={x} y={34 - i * 8} width="28" height={8 + i * 8} rx="3" fill={PURPLE} opacity={0.25 + i * 0.25} data-id="element-956" data-mp="3e136neqencq0" />
            </g>)}
        </svg>;
    case 'pillars':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-957" data-mp="22hqrbrjfzxc2">
          {[12, 38, 64, 90].map(x => <rect key={x} x={x} y="10" width="18" height="28" rx="3" fill={PURPLE} opacity="0.3" data-id="element-958" data-mp="1q1pxc3o9z9ip" />)}
          <rect x="8" y="40" width="104" height="3" rx="1.5" fill={PURPLE} data-id="element-959" data-mp="24z9yezi1wkjl" />
        </svg>;
    case 'contract':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-960" data-mp="2006szdy5scb3">
          <rect x="10" y="6" width="34" height="36" rx="4" fill={PURPLE} opacity="0.12" data-id="element-961" data-mp="1c99tj0njv7gq" />
          <rect x="16" y="13" width="22" height="4" rx="2" fill={PURPLE} opacity="0.5" data-id="element-962" data-mp="17bhyhxrlz58b" />
          <rect x="16" y="21" width="16" height="4" rx="2" fill={PURPLE} opacity="0.3" data-id="element-963" data-mp="1lzmbqm7w6ojp" />
          {[10, 22, 34].map(y => <g key={y} data-id="element-964" data-mp="1h84hsi4ado88">
              <circle cx="60" cy={y + 2} r="3.5" fill={PURPLE} data-id="element-965" data-mp="1h7ursuj8rh2j" />
              <rect x="70" y={y} width="42" height="5" rx="2.5" fill={PURPLE} opacity="0.25" data-id="element-966" data-mp="tis4b61xeu04" />
            </g>)}
        </svg>;
    case 'loop':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-967" data-mp="1e8vwv7jipqx5">
          <ellipse cx="60" cy="24" rx="44" ry="16" fill="none" stroke={PURPLE} strokeOpacity="0.25" strokeWidth="2.5" data-id="element-968" data-mp="1e6jggyterom7" />
          {[16, 60, 104].map(x => <circle key={x} cx={x} cy="24" r="5" fill={PURPLE} data-id="element-969" data-mp="1n28zj17dw6wf" />)}
          <circle cx="60" cy="8" r="4" fill={PURPLE} opacity="0.4" data-id="element-970" data-mp="1s0kchksz6xiv" />
          <circle cx="60" cy="40" r="4" fill={PURPLE} opacity="0.4" data-id="element-971" data-mp="1s1vfvjiie39g" />
        </svg>;
    case 'snak':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-972" data-mp="zats4u0r2zeb">
          <rect x="42" y="4" width="36" height="40" rx="6" fill={PURPLE} opacity="0.12" data-id="element-973" data-mp="zd6invkkqi2z" />
          <rect x="48" y="11" width="24" height="14" rx="3" fill={PURPLE} opacity="0.55" data-id="element-974" data-mp="14cj369nz3d2e" />
          <rect x="48" y="29" width="24" height="4" rx="2" fill={PURPLE} opacity="0.3" data-id="element-975" data-mp="197gclhp5zoft" />
          <rect x="48" y="36" width="14" height="4" rx="2" fill={PURPLE} opacity="0.3" data-id="element-976" data-mp="2fpd97jhf99ej" />
          <path d="M28 24 H 12" stroke={PURPLE} strokeWidth="2" strokeLinecap="round" strokeDasharray="4 3" data-id="element-977" data-mp="2kn4zya6byph5" />
          <path d="M92 24 H 108" stroke={PURPLE} strokeWidth="2" strokeLinecap="round" strokeDasharray="4 3" data-id="element-978" data-mp="90l0sjj62r5h" />
        </svg>;
    case 'moat':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-979" data-mp="3vg5pgihc2osm">
          <circle cx="60" cy="24" r="20" fill="none" stroke={PURPLE} strokeOpacity="0.2" strokeWidth="2" data-id="element-980" data-mp="3qi47k0vn8viq" />
          <circle cx="60" cy="24" r="12" fill={PURPLE} opacity="0.85" data-id="element-981" data-mp="3mfzdx2573yw3" />
          {[[60, 4], [80, 24], [60, 44], [40, 24]].map(([cx, cy]) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="4.5" fill={PURPLE} opacity="0.45" data-id="element-982" data-mp="3chjp55tn51pr" />)}
        </svg>;
    case 'stages':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-983" data-mp="37l2ya3v2498z">
          {[[8, 10], [36, 20], [64, 30], [92, 40]].map(([x, h], i) => <rect key={x} x={x} y={42 - h} width="20" height={h} rx="3" fill={PURPLE} opacity={0.2 + i * 0.22} data-id="element-984" data-mp="15psjzkwbco68" data-mpi={i} />)}
        </svg>;
    case 'sliders':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-985" data-mp="jngpemomsojm">
          {[12, 24, 36].map((y, i) => <g key={y} data-id="element-986" data-mp="5s4t4autuq0l" data-mpi={i}>
              <rect x="10" y={y} width="100" height="4" rx="2" fill={PURPLE} opacity="0.18" data-id="element-987" data-mp="9v169m1lltx5" />
              <circle cx={30 + i * 30} cy={y + 2} r="6" fill={PURPLE} data-id="element-988" data-mp="3s1fmxsr94hz8" />
            </g>)}
        </svg>;
    case 'bars':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-989" data-mp="w60uc6donuw">
          {[[12, 8], [38, 18], [64, 28], [90, 38]].map(([x, h]) => <rect key={x} x={x} y={40 - h} width="18" height={h} rx="3" fill={PURPLE} opacity="0.75" data-id="element-990" data-mp="3n2cncnu1j3yn" />)}
          <path d="M21 36 L 47 30 L 73 20 L 99 6" fill="none" stroke="#0f172a" strokeOpacity="0.35" strokeWidth="2" strokeLinecap="round" data-id="element-991" data-mp="1h7a1nvhn3is4" />
        </svg>;
    case 'close':
      return <svg viewBox="0 0 120 48" className={common} aria-hidden="true" data-id="element-992" data-mp="35qe6sbx0odk2">
          <rect x="10" y="14" width="60" height="7" rx="3.5" fill={PURPLE} data-id="element-993" data-mp="31sqap5jl8eql" />
          <rect x="10" y="27" width="38" height="7" rx="3.5" fill={PURPLE} opacity="0.3" data-id="element-994" data-mp="3fjlkck1wuzv8" />
          <circle cx="98" cy="24" r="12" fill={PURPLE} opacity="0.12" data-id="element-995" data-mp="3arld06jih4om" />
          <circle cx="98" cy="24" r="5" fill={PURPLE} data-id="element-996" data-mp="3pgq124czm34n" />
        </svg>;
    default:
      return null;
  }
}
function FrameCard({
  frame
}: {
  frame: StoryboardFrame;
}) {
  return <article className="storyboard-card rounded-2xl border border-slate-200 bg-white p-4 flex flex-col" data-id="element-997" data-mp="3kjzinadpmvg2">
      <div className="flex items-start justify-between gap-3" data-id="element-998" data-mp="29ntdbz3uy8c">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#6C2BD9] text-xs font-bold text-truewhite flex-shrink-0" data-id="element-999" data-mp="3tiuqeqwekdlh">
          {frame.number}
        </span>
        <p className="text-[9px] uppercase tracking-[0.18em] font-bold text-slate-400 text-right leading-tight pt-1" data-id="element-1000" data-mp="1z5lnjhyd3qc7">
          {frame.eyebrow}
        </p>
      </div>

      <div className="h-12 mt-3 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2" data-id="element-1001" data-mp="34lu6tw0gz4kn">
        <Motif variant={frame.motif} data-id="element-1002" data-mp="30kgxvd22yl9i" />
      </div>

      <h3 className="text-sm font-bold text-slate-900 leading-snug mt-3" data-id="element-1003" data-mp="30la7qmwc5qvv">
        {frame.title}
      </h3>
      <p className="text-[11px] text-slate-600 leading-relaxed mt-1.5" data-id="element-1004" data-mp="2vni5ayy2emdf">
        {frame.takeaway}
      </p>

      <ul className="mt-2.5 space-y-1 border-t border-slate-100 pt-2.5" data-id="element-1005" data-mp="3se0w6b7s2bak">
        {frame.points.map(p => <li key={p} className="flex gap-1.5 text-[10px] text-slate-500 leading-snug" data-id="element-1006" data-mp="3ocp5e2d63k0s">
            <span className="text-[#6C2BD9] font-bold flex-shrink-0" data-id="element-1007" data-mp="3jhi4f2ba8tfk">·</span>
            <span data-id="element-1008" data-mp="3ek06s82pu0mv">{p}</span>
          </li>)}
      </ul>
    </article>;
}
export function Storyboard({
  open,
  onClose
}: StoryboardProps) {
  return <AnimatePresence data-id="element-1009" data-mp="b39l6kunhf02">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 md:p-6 print:p-0 print:bg-white" onClick={onClose} data-id="element-1010" data-mp="b28aihhjquiv">
          <motion.div initial={{
        scale: 0.97,
        opacity: 0,
        y: 16
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.97,
        opacity: 0,
        y: 16
      }} transition={{
        type: 'spring',
        damping: 26,
        stiffness: 300
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[94vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1011" data-mp="wez0pnrctru9">
            {/* Screen-only toolbar */}
            <div className="print-hide flex items-center justify-between gap-4 px-5 md:px-8 py-4 border-b border-slate-200" data-id="element-1012" data-mp="3l5r9m9kfuimp">
              <div className="flex items-center gap-3" data-id="element-1013" data-mp="3h6ifh3lhkiqm">
                <div className="w-10 h-10 rounded-xl bg-[#6C2BD9] flex items-center justify-center flex-shrink-0" data-id="element-1014" data-mp="3uw2fi1non1w8">
                  <LayoutGridIcon className="w-5 h-5" style={{
                color: '#ffffff'
              }} data-id="element-1015" data-mp="3q5djqa4yzira" />
                </div>
                <div data-id="element-1016" data-mp="3ozmd4oaaljd">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#6C2BD9] font-semibold" data-id="element-1017" data-mp="3pj2cfudizur">
                    Snakker · Investor Pitch
                  </p>
                  <h2 className="text-lg md:text-xl font-bold text-slate-900 leading-tight" data-id="element-1018" data-mp="cqh7j1gjtdex">
                    Visual Storyboard
                  </h2>
                </div>
              </div>

              <div className="flex items-center gap-2" data-id="element-1019" data-mp="2eivre62nkrxz">
                <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#6C2BD9] text-sm font-bold shadow-lg shadow-[#6C2BD9]/30 hover:bg-[#5A23B8] hover:-translate-y-0.5 active:translate-y-0 transition-all" style={{
              color: '#ffffff'
            }} data-id="element-1020" data-mp="2ekgjzq5eq0t7">
                  <DownloadIcon className="w-4 h-4" style={{
                color: '#ffffff'
              }} data-id="element-1021" data-mp="p6ysudjm0jws" />
                  <span style={{
                color: '#ffffff'
              }} data-id="element-1022" data-mp="g6k4f5bcmop2">
                    Save as PDF
                  </span>
                </button>
                <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors" aria-label="Close storyboard" data-id="element-1023" data-mp="6caa3i2ujgbp">
                  <XIcon className="w-5 h-5 text-slate-600" data-id="element-1024" data-mp="1bwj4x7p1xqo8" />
                </button>
              </div>
            </div>

            {/* Printable area */}
            <div id="storyboard-print" className="overflow-y-auto px-5 md:px-8 py-6 bg-white" data-id="element-1025" data-mp="11zoaazvwgaf8">
              {/* Print-only header */}
              <div className="hidden print:block mb-4" data-id="element-1026" data-mp="x2o0qezucpjd">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6C2BD9]" data-id="element-1027" data-mp="t01f4vtrk0um">
                  Snakker · Investor Pitch
                </p>
                <h1 className="text-xl font-bold text-slate-900" data-id="element-1028" data-mp="j96iqymszkvg">
                  Visual Storyboard — 18 frames
                </h1>
              </div>

              <p className="print-hide text-xs text-slate-500 mb-5 max-w-2xl leading-relaxed" data-id="element-1029" data-mp="ebn4vzu0pa41">
                The full narrative arc of the deck, frame by frame — the problem,
                the solution, and the business. Save as PDF to share the story
                without presenting it live.
              </p>

              <div className="storyboard-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-id="element-1030" data-mp="2gytt0tdesbsv">
                {storyboardFrames.map(frame => <FrameCard key={frame.number} frame={frame} data-id="element-1031" data-mp="2pygwdkpp7rok" />)}
              </div>

              <p className="text-[10px] text-slate-400 mt-6" data-id="element-1032" data-mp="2uvgyjkjfgaae">
                Snakker · Campaign Service Management for legacy pharma ·
                mh@snakker.io
              </p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}